import { ReportPerformanceDrilldownModel } from '@/models/groups/class';
import { TimespentData } from '@/models/groups/timespent';
import { appConfigService } from '@/providers/services/app/app-config';
import { sortByProperty, roundTwoDigits } from '@/utils/utils';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component({
    name: 'drilldown-performance-pullup',
})
export default class DrilldownPerformancePullup extends Vue {

    // ------------------------------------------------------
    // Properties

    @Prop()
    private performanceDrilldown!: any;

    @Prop()
    private groupType!: string;

    @Prop()
    private groupId!: string;

    @Prop()
    private isAtInitialLevel!: boolean;

    @Prop()
    private selectedSubject: any;

    @Prop()
    private groupLabel: any;

    @Prop()
    private subSelection!: string;

    @Prop()
    private isLoading!: boolean;

    @Prop()
    private activeSubject: any;
    @Prop()
    private activeMonth!: string;
    @Prop()
    private activeYear!: string;

    @Prop()
    private isPdfDownload!: boolean;

    @Prop()
    private activeSortKey!: string;

    @Prop()
    private isSchoolLevel!: boolean;

    private searchTerms: any = '';

    private isLoader: boolean = false;

    private CSVreport: any = [];

    private tableHeader: any = [
        {
            label: 'Grade',
            key: 'gradeName',
        },
        {
            label: 'Class',
            key: 'name',
        },
        {
            label: 'Total Competencies',
            key: 'totalCompetencies',
        },
        {
            label: 'Students Gained',
            key: 'totalCompetencyGained',
        },
        {
            label: 'Student Inprogress',
            key: 'totalCompetencyInProgress',
        },
        {
            label: 'Avg competency gain per child',
            key: 'averageCompetencyGained',
        },
    ];

    private tableColumn: any = ['totalCompetencies',
        'totalCompetencyGained', 'totalCompetencyInProgress', 'averageCompetencyGained'];

    private filteredContent: any = [];

    private sortingByList: any = [{
        name: 'Sort By Grade',
        key: 'sort_grade',
    },
    {
        name: 'Sort By Level',
        key: 'sort_level',
    }];

    private activeSort: any = this.sortingByList[1];

    private get activeGroupType() {
        const filteredContent = this.performanceDrilldown;
        const accessibleLevels = appConfigService.getUserGroupAccessLevel();
        const activeLevel = accessibleLevels.find((item: any) => item.type === filteredContent.type);
        return activeLevel ? activeLevel.name : filteredContent.type;
    }

    @Watch('performanceDrilldown')
    private watchDrilldownData() {
        this.filteredContent = [...this.performanceDrilldown.drilldown];
        this.loadSortingData();
    }

    // ------------------------------------------------------
    // Hooks

    private mounted() {
        if (this.activeSortKey) {
            this.activeSort = this.sortingByList.find((item: any) => item.key === this.activeSortKey);
        }
        this.loadSortingData();
    }


    // ------------------------------------------------------
    // Actions

    private onSelectRow(activeRow: any) {
        this.$emit('onSelectLevel', activeRow, 'performance');
    }

    private onGoupward() {
        this.$emit('onGoupward');
    }

    private onDownloadPdf() {
        this.$emit('onDownloadPdf');
    }

    private onSearch() {
        this.filteredContent = this.searchDrilldown();
    }

    private onSelectSort(option: any) {
        this.activeSort = option;
        this.$emit('onSelectSort', option);
        this.loadSortingData();
    }

    // --------------------------------------------------------------
    // Methods

    private searchDrilldown() {
        const searchTerms = this.searchTerms;
        const activeSort = this.activeSort;
        const performanceDrilldown = this.loadSortingData();
        return [...performanceDrilldown].filter((drilldown) => {
            return drilldown[activeSort.key === 'sort_level' ? 'name' : 'gradeName'].toLowerCase()
                .startsWith(searchTerms.toLowerCase());
        });
    }

    private loadSortingData() {
        const activeSort = this.activeSort;
        this[activeSort.key === 'sort_grade' ? 'sortByGrade' : 'sortByLevel']();
        return this.filteredContent;
    }

    private sortByLevel() {
        const filteredContent = this.performanceDrilldown.drilldown ?
            JSON.parse(JSON.stringify(this.performanceDrilldown.drilldown)) : [];
        filteredContent.forEach((item: ReportPerformanceDrilldownModel) => {
            const gradeList = item.drilldown || [];
            const groupGradeIds = gradeList.map((gradeItem: ReportPerformanceDrilldownModel) => gradeItem.gradeId);
            const uniqueGroupIds = [...new Set(groupGradeIds)];
            const gradeOverallStats: ReportPerformanceDrilldownModel[] = [];
            uniqueGroupIds.forEach((gradeId) => {
                const filterGradeById = gradeList.filter((grade: ReportPerformanceDrilldownModel) => {
                    return grade.gradeId === gradeId;
                }).reduce(
                    (gradeItem: ReportPerformanceDrilldownModel, gradeData: ReportPerformanceDrilldownModel) => {
                        gradeItem.averageCompetencyGained = Math.round(
                            (gradeItem.averageCompetencyGained + gradeData.averageCompetencyGained) / 2);
                        gradeItem.totalCompetencies = gradeItem.totalCompetencies + gradeData.totalCompetencies;
                        gradeItem.totalCompetencyGained =
                            gradeItem.totalCompetencyGained + gradeData.totalCompetencyGained;
                        gradeItem.totalCompetencyInProgress =
                            gradeItem.totalCompetencyInProgress + gradeData.totalCompetencyInProgress;
                        return gradeItem;
                    });
                gradeOverallStats.push(filterGradeById);
            });
            sortByProperty(gradeOverallStats, 'gradeId', 'ASC');
            item.drilldown = gradeOverallStats;
        });
        this.filteredContent = filteredContent;
    }

    private sortByGrade() {
        const filteredContent = JSON.parse(JSON.stringify(this.performanceDrilldown.drilldown));
        const gradeStats: ReportPerformanceDrilldownModel[] = [];
        // Helps to clone and aggregate the competency count
        const cloneObj = (gradeItem: any, levelItem: any) => {
            const cloneGradeObj = { ...gradeItem };
            const cloneLevelObj = { ...levelItem };
            cloneLevelObj.drilldown = [];
            cloneGradeObj.drilldown = [];
            cloneLevelObj.averageCompetencyGained = gradeItem.averageCompetencyGained;
            cloneLevelObj.totalCompetencies = gradeItem.totalCompetencies;
            cloneLevelObj.totalCompetencyGained = gradeItem.totalCompetencyGained;
            cloneLevelObj.totalCompetencyInProgress = gradeItem.totalCompetencyInProgress;
            return cloneLevelObj;
        };
        filteredContent.forEach((levelItem: ReportPerformanceDrilldownModel) => {
            levelItem.drilldown!.forEach((gradeItem) => {
                const gradeLevels = gradeStats.find((grade) => grade.gradeId === gradeItem.gradeId);
                if (gradeLevels) {
                    const levelObj = gradeLevels.drilldown!.find((level) => level.id === levelItem.id);
                    if (levelObj) {
                        levelObj.averageCompetencyGained =
                            (levelObj.averageCompetencyGained + gradeItem.averageCompetencyGained) / 2;
                        levelObj.totalCompetencies = levelObj.totalCompetencies + gradeItem.totalCompetencies;
                        levelObj.totalCompetencyGained =
                            levelObj.totalCompetencyGained + gradeItem.totalCompetencyGained;
                        levelObj.totalCompetencyInProgress =
                            levelObj.totalCompetencyInProgress + gradeItem.totalCompetencyInProgress;
                    } else {
                        gradeLevels.drilldown!.push(cloneObj(gradeItem, levelItem));
                    }
                } else {
                    const cloneGradeObj = { ...gradeItem };
                    cloneGradeObj.drilldown!.push(cloneObj(gradeItem, levelItem));
                    gradeStats.push(cloneGradeObj);
                }
            });
        });
        sortByProperty(gradeStats, 'gradeId', 'ASC');
        this.filteredContent = gradeStats;
    }
    private async GenerateCSVReport(content: any) {
        let index = 0;
        const csvData: any = [];
        const header = [
            this.activeSort.key === 'sort_level' ? this.groupLabel : 'Grade',
            this.activeSort.key === 'sort_level' ? 'Grade' : this.groupLabel,
            'Total Competencies',
            'No.Of Students With Gains',
            'No.Of Students In Progress',
            'AVG Competency Gain per  Student',
        ];

        csvData.push(header);
        index++;
        csvData.push('\n');
        await content.forEach(async (con: any) => {
            const name = con.name ? con.name : '-';
            const gradeLevelGradename = con.gradeName ? con.gradeName : '-';
            if (con.drilldown && con.drilldown.length) {
                await con.drilldown.forEach((drill: any, drillIdx: any) => {
                    const clusterName = drill.name ? drill.name : '-';
                    const gradeName = drill.gradeName ? drill.gradeName : '-';
                    const totalCompetencies = drill.totalCompetencies ? drill.totalCompetencies : '-';
                    const totalCompetencyGained = drill.totalCompetencyGained ? drill.totalCompetencyGained : '-';
                    const totalCompetencyInProgress = drill.totalCompetencyInProgress ?
                        drill.totalCompetencyInProgress : '-';
                    const averageCompetencyGained = drill.averageCompetencyGained ? drill.averageCompetencyGained : '-';
                    if (this.activeSort.key === 'sort_level') {
                        csvData.push([drillIdx === 0 ? name : '', gradeName, totalCompetencies,
                            totalCompetencyGained, totalCompetencyInProgress, averageCompetencyGained]);
                    } else {
                        csvData.push([gradeLevelGradename, clusterName, totalCompetencies,
                            totalCompetencyGained, totalCompetencyInProgress, averageCompetencyGained]);
                    }
                    index++;
                    csvData.push('\n');
                });
            } else {
                csvData.push([name, '-', '-', '-', '-', '-']);
            }
            csvData.push('\n');
        });

        this.CSVreport = csvData;
        const CSVfileName = this.performanceDrilldown.name + '-' + this.activeMonth + '-' + this.activeYear + '.csv';
        const csvDownload = new Blob(this.CSVreport, { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(csvDownload);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', CSVfileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

    }

    private roundTwoDigitsNumber(value: any) {
        return roundTwoDigits(value);
    }

}
